import { debounce } from 'debounce';
import { isElementVisible, getClosest, onAttach, onDetach, isDetached } from '../utils/DomUtils';

export const RENDER_DEBOUNCE_INTERVAL = 50;
export const FOCUS_FIRST_MAX_TRIES = 2400;
const DATA_TAB_ORDER = 'data-tab-order';
const NESTED_TABBING_SELECTOR = `[${DATA_TAB_ORDER}]`;
const TAB_UTIL_UPDATE_EVENT = 'TabManagementUtil-Update';
const SHOULD_FIND_PARENT = 'data-should-find-parent';

/**
 * A vanilla JS utility for managing tabbing within a containing element, so that tabbing does not escape that element.
 * e.g. fields and buttons in a dialog that should not allow tabbing to escape that dialog and "leak" to parts of
 * the UI behind the dialog.
 */
export class TabManagementUtil {
  private list: HTMLElement[] = [];
  private nestedUtils: TabManagementUtil[] = [];
  private observer: MutationObserver;
  private focusFirstAttempts = 0;

  protected defaultObserverOptions = {
    attributes: true,
    childList: true,
    subtree: true,
    attributeFilter: ['style', 'tabindex'], //we need to observe the style attributes cause on form it is possible controls are getting hidden/visible via rule.
  };

  get observerOptions() {
    return this.defaultObserverOptions;
  }
  // private nestedControllers: TabManagementUtil[];
  public parentControl: TabManagementUtil | undefined;

  /**
   * Set to true if this is a child utility, meaning it should allow tabbing to continue,
   * out of the container, rather than creating the loop.
   */
  public get isNested() {
    return !!this.parentControl;
  }

  public shouldBeConsideredInTabbing(node: HTMLElement) {
    const isVisible = IS_TEST ? true : isElementVisible(node as HTMLElement);
    const isEnabled = !(node as HTMLInputElement).disabled;
    return isVisible && isEnabled;
  }

  /**
   * This is intended to be 1:1 between util and the container element, for lifecycle of that element.
   *
   * Prefer `ensureFormTabManagement` factory over this constructor.
   *
   * @param container whose elements to tab loop
   * @param shouldFocusFirst true to focus the first element when this is constructed
   */
  constructor(private container: HTMLElement, private shouldFocusFirst = true, private trapTabbing = true) {
    // Use a mutation observer to track when the set of tab-targets changes within the container,
    // elements being collapsed in a property grid category, or new fields being added.
    this.observer = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          this.updateTabList();
          this.focusFirstElement(); // in case this runs during initial render and elements are out of order.
        }
      });
    });

    // wait until DOM element is attached to add these other listeners, to avoid scenario where things are never attached, or added and removed very quickly.
    onAttach(container, () => {
      this.updateTabList(); // update tab list on attach, since observer won't be listening to mutations prior to attach.
      this.focusFirstElement(); // NOOP if shouldFocusFirst is false

      // Clean self on detach to avoid memory leak scenarios for developers to be mindful of. Scenario is the container
      // being detached within the debouncedFocusFirst interval. The timer keeps a reference to the DOM element and
      // util, and prevents either from being garbage collected. Cleaning up on detach will avoid all of this,
      // and avoids React devs having to remember to clean these up in hooks.
      onDetach(container, this.cleanupInstance);

      if (!trapTabbing) return; //this is useful where we do not want to trap the tabbing but just want to focus the first element, curretly getting used in the forms

      this.observer.observe(container, this.observerOptions);

      this.container.addEventListener('keydown', this.punchTabbing);

      // listen for update events for edge cases (like things changing inside an iframes...ckeditor) MutationObserver catch.
      this.container.addEventListener(TAB_UTIL_UPDATE_EVENT, this.updateEventHandler);
    });

    // bind this utility to the container in case a parent needs to inspect it.
    bindUtil(container, this);
  }

  /**
   * Triggers an update of the first/last elements to manage tabbing on.
   * You should only need to call this manually if you know you are changing _attributes_ of elements,
   * since this handles adding/removing elements on its own.
   */
  public updateTabList() {
    this.doUpdateList.clear();
    this.doUpdateList();
  }

  /**
   * Recursive call to find all the nested utils, only add the parent elements in the tab targets.
   * For example - If element or util is present in another util then it will not be added it in the
   * tab targets.
   * It also sorts the nested util based on data-tab-order.
   */
  private addNestedUtil(nestedUtil: TabManagementUtil, tabTargets: HTMLElement[] = []) {
    const nestedTabContainers: HTMLElement[] = [];
    // find all nested tab containers
    const allNestedTabContainers = Array.from(nestedUtil.container.querySelectorAll(NESTED_TABBING_SELECTOR));
    allNestedTabContainers.forEach(el => {
      const tabIndex = getTabIndex(el);
      const util = getBoundUtil(el);
      if (util) {
        // check if it is already been added to nested util
        if (this.nestedUtils.includes(util) === false) {
          util.parentControl = nestedUtil;
          this.nestedUtils.push(util);
          // add to tab targets only if tab-order or tab-index is greater or equal to zero and the tab targets has atleast one child with tabindex
          tabIndex >= 0 &&
            this.shouldBeConsideredInTabbing(el as HTMLElement) &&
            el.querySelectorAll('[tabindex]').length > 0 &&
            tabTargets.push(el as HTMLElement);
          nestedTabContainers.push(el as HTMLElement);
        }

        util.list.sort((a, b) => {
          const aIndex = getTabIndex(a);
          const bIndex = getTabIndex(b);
          return aIndex - bIndex;
        });

        // check for util inside the nested util
        nestedTabContainers.push(...this.addNestedUtil(util));
      }
    });

    return nestedTabContainers;
  }

  private doUpdateList = debounce(() => {
    const tabTargets: HTMLElement[] = [];
    this.list = [];
    this.nestedUtils = [];

    const nestedTabContainers = this.addNestedUtil(this, tabTargets);

    this.container.querySelectorAll('[tabindex]').forEach(node => {
      // using webpack feature flag to bypass isElementVisible for unit tests

      if (
        getTabIndex(node) >= 0 &&
        this.shouldBeConsideredInTabbing(node as HTMLElement) &&
        isNotNestedControl(node, nestedTabContainers)
      ) {
        // if it has no offset height it's invisible
        tabTargets.push(node as HTMLElement);
      }
    });

    // Sort ascending, but preserve behavior where browser focuses 0 after the last positive value.
    // This is consistent with 6.x and https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex
    tabTargets
      .sort((a, b) => {
        const aIndex = getTabIndex(a);
        const bIndex = getTabIndex(b);
        return aIndex === 0 ? 1 : bIndex === 0 ? -1 : aIndex - bIndex;
      })
      .forEach(el => {
        const specialTarget = getSpecialTabEventTarget(el);
        el = specialTarget || el;
        if (!this.list.includes(el)) {
          // catch cases where usually special targets are nested under more than one parent with a tab index
          // like... you guessed it... CKEditor
          this.list.push(el);
        }
      });
  }, RENDER_DEBOUNCE_INTERVAL);

  // Previous iteration of this attempted to allow normal browser behavior except for first/last
  // elements in the tab order, but this broke down because of CKEditor, and forms elements with tabindex of 0.
  // for this to work it really has to manually control tabbing on the container the whole time.
  protected punchTabbing = (e: KeyboardEvent, target?: HTMLElement): void => {
    // use keycode fallback for IE. this is vanilla JS so no React normalization to help us here.

    const current = target || e.target;

    if ((e.key === 'Tab' || e.keyCode === 9) && current) {
      let currentTarget = current as HTMLElement;
      let focusIndex = this.list.indexOf(currentTarget);
      let findParentIfTargetIsNotFound = currentTarget.getAttribute(SHOULD_FIND_PARENT) ? true : false;
      //If currentTarget is not present in the tab list and findParentIfTargetIsNotFound is true,
      //it will search if parent is present in the tabable list or not
      if (focusIndex === -1 && findParentIfTargetIsNotFound) {
        const parent = this.list.find(l => l.contains(currentTarget));
        if (parent) focusIndex = this.list.indexOf(parent);
      }
      if (focusIndex === -1) return; //this element is not in the tabbing list, leave it alone.
      e.preventDefault();
      e.stopPropagation(); // prevent bubbling to parent tab utils

      // cases for escaping a nested control:
      if (this.isNested) {
        if (e.shiftKey && focusIndex === 0) {
          // if event is first or last element in a nested container,
          // let the event bubble to the parent utility, which will handle the event:
          return this.parentControl?.punchTabbing(e, this.container);
        } else if (!e.shiftKey && focusIndex === this.list.length - 1) {
          return this.parentControl?.punchTabbing(e, this.container);
        }
      }

      // normal behavior:
      if (e.shiftKey) {
        focusIndex = focusIndex - 1 < 0 ? this.list.length - 1 : focusIndex - 1;
      } else {
        focusIndex = focusIndex + 1 === this.list.length ? 0 : Math.min(focusIndex + 1, this.list.length - 1);
      }

      const nextTarget = this.list[focusIndex];
      const nestedTabUtil = getBoundUtil(nextTarget);

      // case to enter a nested control:
      if (nestedTabUtil && nestedTabUtil.list.length > 0) {
        // instead of focusing a nested container element, or one of it's children, delegate focus control to the nested util.
        // this is because of controls like data-repeaters can have a data-tab-order of 7, with internal tab orders of 1,2, etc.
        if (e.shiftKey) {
          nestedTabUtil.focusLast();
        } else {
          nestedTabUtil.focusFirst();
        }
      } else {
        if (nextTarget) {
          if (nextTarget.tabIndex >= 0) {
            nextTarget.focus();
          } else {
            this.punchTabbing(e, nextTarget);
          }
        }
      }
    }
  };

  /**
   * Cancels previous debounced call if in flight, and conditionally calls `debouncedFocusFirst` again,
   * after assignment of the `focusFirst` argument. If the container element is not yet attached, this will
   * simply assign the `shouldFocusFirst` field to be read by the handler added in the constructor.
   *
   * @param focusFirst assigns instance field if `true` or `false`. Does not assign if `undefined`.
   */
  public focusFirstElement = (focusFirst?: boolean) => {
    this.debouncedFocusFirst.clear();

    if (focusFirst !== undefined) {
      this.shouldFocusFirst = focusFirst;
    }

    if (!this.shouldFocusFirst) {
      return;
    }

    if (isDetached(this.container)) {
      // await the attach callback to trigger first focus later.
      return;
    }

    this.debouncedFocusFirst();
  };

  /**
   * Focus last element in the set. Because the use-case of this is different (nested controls, not initial render),
   * it's not debounced.
   */
  protected focusLast = () => {
    this.focusElement(this.list.length - 1);
  };

  /**
   * Focus first element in the set. Because the use-case of this is different (nested controls, not initial render),
   * it's not debounced.
   */
  protected focusFirst = () => {
    this.focusElement(0);
  };

  /**
   * This action is debounced, partly as a timeout to allow for rendering time, but also so we can
   * spam it in case things are rendering out of order.
   */
  private debouncedFocusFirst = debounce(() => {
    if (this.shouldFocusFirst) {
      if (this.list[0]) {
        this.focusElement();
      } else {
        // increment now many recursed attempts, just in case.
        // This will help us identify places we have used this in a way that creates a memory leak, if it happens.
        this.focusFirstAttempts++;
        if (this.focusFirstAttempts > FOCUS_FIRST_MAX_TRIES) {
          // 2400 attempts at 50ms would be 2 mins. If there is still nothing to focus after 2 mins, the user is already sad.
          console.warn(
            'A TabManagementUtil has tried to focus its first element for 2 minutes.\nThis is either a very slow loading form, or indicates a memory leak.\n Container:',
            this.container
          );
          return;
        }
        this.debouncedFocusFirst();
      }
    }
  }, RENDER_DEBOUNCE_INTERVAL);

  private focusElement = (index = 0) => {
    // we're immediately focusing an element here, so cancel any initial render focus first:
    this.shouldFocusFirst = false;
    this.debouncedFocusFirst.clear();
    if (this.list[index]) {
      const nestedControl = getBoundUtil(this.list[index]);
      if (nestedControl) {
        // this element is a nested control, defer to the nested control to focus _its_ first element:
        // this is already debounced once, so we won't do it again unless we find we have to.
        nestedControl.focusFirstElement(true);
      } else {
        // in case we queued focus first in a nested util while things were rendering, but now everything is rendered:
        this.nestedUtils.forEach(util => {
          util.shouldFocusFirst = false;
          util.debouncedFocusFirst.clear();
        });

        // actually focus our first element:
        this.list[index].focus();
      }
    }
  };

  private updateEventHandler = () => this.updateTabList();

  public cleanupInstance = () => {
    // clear any debounce timers
    this.debouncedFocusFirst.clear();
    // unsubscribe to any
    window.removeEventListener(TAB_UTIL_UPDATE_EVENT, this.updateEventHandler);
    // cleanup any nested utils as well:
    this.nestedUtils.forEach(s => s.cleanupInstance());
  };

  /**
   * Force tab util(s) to update their list of tab-target elements.
   * This is only necessary for edge cases MutationObserver doesn't catch. (chiefly, things changing inside an iframe...ckeditor) .
   *
   * This method can be spammed, because the the handler method is debounced.
   *
   * @param source child of target tab util container. If provided will bubble up. If not, will affect all utils on the page.
   */
  public static dispatchChangeEvent(source?: HTMLElement) {
    if (source) {
      // bubbling from child to parent limits impact if multiple tab utils are on the screen at once.
      source.dispatchEvent(new Event(TAB_UTIL_UPDATE_EVENT, { bubbles: true }));
    } else {
      window.dispatchEvent(new Event(TAB_UTIL_UPDATE_EVENT));
    }
  }

  public static cleanup = (target: Element) => {
    const util = getBoundUtil(target);
    if (util) {
      util.cleanupInstance();
    }
  };
}

/**
 * A factory function to avoid duplication, and memory leaks that could cause.
 * @param args constructor args of a `TabManagementUtil`
 * @returns either an existing bound utility or creates a new one and returns that.
 */
export function ensureTabManagement(...args: ConstructorParameters<typeof TabManagementUtil>) {
  const util = getBoundUtil(args[0]);
  return util ? util : new TabManagementUtil(...args);
}

/**
 * A factory function to avoid duplication, and memory leaks that could cause.
 * @param args constructor args of a `TabManagementUtil`
 * @returns either an existing bound utility or creates a new one and returns that.
 */
export function ensureFormTabManagement(...args: ConstructorParameters<typeof TabManagementUtil>) {
  const util = getBoundUtil(args[0]);
  return util ? util : new FormTabManagementUtil(...args);
}

/**
 * Helper method to catch special cases where the element with focus won't
 * bubble events to the container element, e.g. iframe / CKEditor.
 * @param element to add  handlers to
 * @returns element or undefined. If nothing is returned, presume container handler is sufficient.
 */
function getSpecialTabEventTarget(element?: HTMLElement) {
  if (!element) {
    return;
  }
  const ckEditorTarget = getCKeditorTarget(element);
  if (ckEditorTarget) {
    return ckEditorTarget;
  }

  return;
}

export class FormTabManagementUtil extends TabManagementUtil {
  public shouldBeConsideredInTabbing(node: HTMLElement) {
    let isTabTarget = super.shouldBeConsideredInTabbing(node);
    // find closest section
    const isDisabled =
      node.className.includes('DisableControl') ||
      getClosest(node, 'DisableControl') ||
      node.getElementsByClassName('isReadOnly').length;
    return isTabTarget && !isDisabled;
  }

  get observerOptions() {
    return {
      ...this.defaultObserverOptions,
      attributeFilter: ['style', 'class'],
    };
  }
}

function getCKeditorTarget(element: HTMLElement) {
  const richTextBoxContainer = element.className.includes('silver-richtextbox-container') ? element : false;

  if (richTextBoxContainer) {
    const iFrame = richTextBoxContainer.querySelector('iframe');
    const ckEditable = iFrame && iFrame.contentWindow?.document.querySelector('body');
    return ckEditable;
  } else if (element.nodeName === 'IFRAME') {
    const ckEditable = element && (element as HTMLIFrameElement).contentWindow?.document.querySelector('body');
    return ckEditable as HTMLElement;
  }
}

function getTabIndex(el: Element | HTMLElement) {
  // include data-tab-order here too, to sort among those with actual tab indexes
  const index = el.getAttribute('tabindex') || el.getAttribute(DATA_TAB_ORDER);
  return index === null ? -1 : parseInt(index, 10);
}

/**
 * Helper to bind instances of this utility to particular DOM nodes, in order to
 * manage nesting of tab-controlled areas with indexes that aren't sequential to their parent.
 *
 * e.g. data-repeaters can have a data-tab-order of 7, with internal tab orders of 1,2, etc...
 * @param el to bind to
 * @param util to bind
 */
function bindUtil(el: HTMLElement, util: TabManagementUtil) {
  if (getBoundUtil(el)) {
    console.error('A Tab Utility is already attached to this element', el);
  }
  (el as { [key: string]: any })['tabUtil'] = util;
}

export function getBoundUtil(el: Node): TabManagementUtil | undefined {
  return (el as { [key: string]: any })['tabUtil'];
}

/**
 * @param el to evaluate
 * @param nestedTabContainers to search through
 * @returns true if the element is not within a nested tab container
 */
function isNotNestedControl(el: Node, nestedTabContainers: Node[]) {
  return !nestedTabContainers.find(node => {
    return node.contains(el);
  });
}
